import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { DARK_SCHEME_QUERY, initialTheme, rememberThemeChoice, storedTheme, type Theme } from "./theme-preference";

/**
 * The app's theme (ADR-WBC1). Starts where the head script started
 * (src/lib/theme-preference.ts), follows the operating system while the
 * visitor has not chosen, and stores only an explicit choice made with a
 * theme toggle. Every toggle in the app receives `chooseTheme`.
 */
export function useThemePreference(): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => (typeof window === "undefined" ? "light" : initialTheme(window)));

  // <html data-theme> drives the body background, the warm-splash colours and
  // the load-error message. Update it before paint, in the same frame as the
  // app shell, so no frame shows the two themes mixed.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const follow = () => {
      if (storedTheme(window) === null) setTheme(query.matches ? "dark" : "light");
    };
    query.addEventListener?.("change", follow);
    return () => query.removeEventListener?.("change", follow);
  }, []);

  const chooseTheme = useCallback((next: Theme) => {
    rememberThemeChoice(window, next);
    setTheme(next);
  }, []);

  return [theme, chooseTheme];
}
